import { LANGUAGE } from "../constants";
import * as CONSTANTS from "../constants/actions/myanalog";
import { baseAxiosAPI } from "../service/api.service";

/**
 * @name openModal
 * @function
 * @description Opens the modal.
 */
export const openModal = () => {
  return (dispatch) => dispatch({ type: CONSTANTS.SHOW_MODAL });
};

/**
 * @name closeModal
 * @function
 * @description Closes the modal.
 */
export const closeModal = () => {
  return (dispatch) => {
    dispatch({ type: CONSTANTS.HIDE_MODAL });
  };
};

/**
 * @name setModalState
 * @function
 * @description Set the value of all the fields in the modal.
 * @param {Object} parameters
 */
export const setModalState = (parameters) => {
  return (dispatch) =>
    dispatch({ type: CONSTANTS.SET_VALUES, payload: parameters });
};

/**
 * @name setSuccessState
 * @function
 * @description Set the success state in the modal.
 * @param {Object} state
 */
export const setSuccessState = (state) => {
  return (dispatch) =>
    dispatch({ type: CONSTANTS.SET_SUCCESS, payload: state });
};

/**
 * @name setModalError
 * @function
 * @description Set the error state of modal.
 * @param {string} error
 */
export const setModalError = (error) => {
  return (dispatch) => dispatch({ type: CONSTANTS.SET_ERROR, payload: error });
};

/**
 * @name setDropdownValues
 * @function
 * @constant
 * @description Set the dropdown values of the modal.
 * @param {Array} data
 */
export const setDropdownValues = (data) => {
  return (dispatch) =>
    dispatch({ type: CONSTANTS.SET_DROPDOWN_VALUES, payload: data });
};

/**
 * @name setTranslationValues
 * @function
 * @constant
 * @description Set the translation values of the modal.
 * @param {Object} data
 */
export const setTranslationValues = (data) => {
  return (dispatch) => {
    dispatch({ type: CONSTANTS.SET_TRANSLATION_VALUES, payload: data });
  };
};

/**
 * @function
 * @param {string} errorM - The error key which needs to be used to get the message from translationsObject.
 * @param {function} dispatch - A function for dispatching actions in a Redux or state management context.
 * @param {Object} translationValue - A function for dispatching actions in a Redux or state management context.
 * @description
 *   This function is responsible for handling common modal errors by mapping the error message
 *   to a translation object and dispatching a Redux action to set the modal error.
 *   It allows for consistent error handling and display in modal dialogs.
 */
const commonModalErrorHandler = (
  errorM,
  dispatch,
  translationValue,
  productName,
  projectName
) => {
  // ERROR-HELP
  if (errorM) {
    let trimmed = errorM.includes("{")
      ? errorM.substring(1, errorM.length - 1).trim()
      : errorM;
    if (translationValue) {
      let errorMessage = translationValue[`${trimmed}`]
        ? translationValue[`${trimmed}`]
        : errorM;
      errorMessage = addingKeywords(errorMessage, {
        productName: productName.toUpperCase(),
        projectName: projectName,
        logo: "LOGO",
      });
      dispatch(setModalError(errorMessage));
    } else {
      dispatch(setModalError(errorM ? errorM : "Some Error Occurred"));
    }
  } else {
    dispatch(setModalError("Some Error Occurred."));
  }
};

/**
 * @name replaceTokens
 * @method
 * @description Replaces tokens within a string with its respective data
 * @returns {string} The properly formatted content
 * @param {string} text String containing tokens to replace
 * @param {Object} data Object of keys whose names that match token names
 * and their values are the content to be injected into the string.
 * *For Example* if `str` contains the token `{test}`, the `{test}`
 * token will be replaced with the value inside `values.test`
 */
const addingKeywords = (text, data) => {
  const replacedText = text
    .replace("{target}", data ? data.projectName : "PROJECT")
    .replace("{Project Name}", data ? data.projectName : "PROJECT")
    .replace("{logo}", data ? data.logo : "LOGO")
    .replace(`\\"myAnalog\\"`, data ? data.logo : "LOGO")
    .replace("{productName}", data ? data.productName : "PRODUCT");
  return replacedText;
};

export const getTranslations = (translationValue) => {
  return (dispatch) =>
    baseAxiosAPI(
      "GET",
      `translations?pageId=a89dc8dd3e864dd39c826c1b3c679be1&languageCode=${LANGUAGE}`,
      null,
      (data) => {
        dispatch(setTranslationValues(data));
      },
      (error) => commonModalErrorHandler(error, dispatch, translationValue),
      dispatch
    );
};

/**
 * @name getProjects
 * @function
 * @constant
 * @param {boolean} init
 * @param {string} token
 * @param {Function} settledCallback
 * @description Calls the API, Gets the Projects that a user has in myAnalog.
 */
export const getProjects = (props) => {
  const { init, translationValue, settledCallback, signal } = props;
  return (dispatch) => {
    dispatch(getTranslations(translationValue));
    baseAxiosAPI(
      "GET",
      "projects?startIndex=0&pageSize=10",
      null,
      (data) => {
        const projectListData = data["projects"];
        let dropdownOptions = [];
        for (const project of projectListData) {
          const projectId = project.id;
          const projectName = project.title;
          dropdownOptions.push({
            id: projectId,
            title: projectName,
          });
        }
        settledCallback();
        // TODO Pagination is not handled, when there are more than 10 projects.
        dispatch(setDropdownValues(dropdownOptions));
        if (init) {
          dispatch(openModal());
        }
      },
      (error) => {
        settledCallback();
        dispatch(setDropdownValues([]));
        commonModalErrorHandler(error, dispatch, translationValue);
        dispatch(openModal());
      },
      dispatch,
      signal,
    );
  };
};

/**
 * @name addToMyAnalogProject
 * @function
 * @constant
 * @description Calls the API, based on response shows error or closes the modal.
 * @param {string} productID
 * @param {string} token
 * @param {boolean} subscription
 */
export const addToMyAnalogProject = (data) => {
  const { subscription, productID, token, translationValue, errorAreaName } =
    data;
  return (dispatch) =>
    baseAxiosAPI(
      "POST",
      "products",
      [productID],
      (data) => {
        if (subscription) {
          dispatch(
            subscribeToMyAnalogProject({ productID: productID, token: token })
          );
        } else {
          dispatch(setSuccessState({ productsSuccessState: productID }));
          dispatch(closeModal());
        }
      },
      (error) => {
        commonModalErrorHandler(
          error,
          dispatch,
          translationValue,
          productID,
          errorAreaName
        );
      },
      dispatch
    );
};

/**
 * @name bookmarkTable
 * @function
 * @constant
 * @description Calls the API, based on response shows error or closes the modal.
 * @param {string} tableTitle
 * @param {string} tableNote
 * @param {string} token
 * @param {string} pageURL
 */
export const bookmarkTable = (data) => {
  const { tableTitle, tableNote, pageURL, translationValue, errorAreaName } =
    data;
  const bookmarkObject = {
    url: pageURL,
    title: tableTitle,
    description: tableNote,
  };
  return (dispatch) =>
    baseAxiosAPI(
      "POST",
      "bookmarks",
      [bookmarkObject],
      (data) => {
        dispatch(setSuccessState({ tableSuccessState: true }));
        dispatch(closeModal());
      },
      (error) =>
        commonModalErrorHandler(
          error,
          dispatch,
          translationValue,
          tableTitle,
          errorAreaName
        ),
      dispatch
    );
};

/**
 * @name subscribeToMyAnalogProject
 * @constant
 * @function
 * @description Subscribes to Project of myAnalog, Calls the API, based on response shows error or closes the modal.
 * @param {string} token
 * @param {string} productID
 */
export const subscribeToMyAnalogProject = (data) => {
  const { productID, projectName, translationValue } = data;
  return (dispatch) =>
    baseAxiosAPI(
      "POST",
      "products/subscription",
      [{ generic: productID }],
      (data) => {
        dispatch(setSuccessState({ productsSuccessState: productID }));
        dispatch(closeModal());
      },
      (error) =>
        commonModalErrorHandler(
          error,
          dispatch,
          translationValue,
          productID,
          projectName
        ),
      dispatch
    );
};

/**
 * @name addToCustomProject/artifact
 * @constant
 * @function
 * @description Saves the product to the custom made Project.
 * @param {string} {productID}
 * @param {string} {projectID}
 * @param {string} {description}
 * @param {number} {artifact}
 * @param {string} token
 * @param {string} {productURL}
 */
export const addToCustomProject = (data) => {
  const {
    productID,
    productURL,
    projectName,
    projectID,
    artifact,
    translationValue,
    description,
  } = data;
  return (dispatch) =>
    baseAxiosAPI(
      "POST",
      `project/${projectID}/artifact`,
      artifact === 3
        ? {
            artifactType: artifact,
            url: productURL,
            description: description,
            title: productID,
          }
        : {
            artifactType: artifact,
            title: productID,
            url: productURL,
          },
      (data) => {
        dispatch(
          setSuccessState(
            artifact === 3
              ? { tableSuccessState: true }
              : { productsSuccessState: productID }
          )
        );
        dispatch(closeModal());
      },
      (error) =>
        commonModalErrorHandler(
          error,
          dispatch,
          translationValue,
          productID,
          projectName
        ),
      dispatch
    );
};

/**
 * @name createProject
 * @constant
 * @function
 * @description Create the product in the myAnalog, and returns the projectID.
 * @param {string} {projectTitle}
 * @param {string} {projectDescription}
 * @param {string} {productID}
 * @param {string} {tableDesc}
 * @param {string} {providedTableName}
 * @param {string} token
 * @param {string} {productURL}
 */
export const createProject = (data) => {
  const {
    productID,
    productURL,
    projectTitle,
    projectDescription,
    translationValue,
    providedTableName,
    tableDesc,
  } = data;
  return (dispatch) =>
    baseAxiosAPI(
      "POST",
      "project",
      {
        title: projectTitle,
        description: projectDescription,
      },
      (data) => {
        /**
         * data format:
         * {message: "Created Project and Added contributors to the project", id: 19127}
         */
        const newProjectID = data ? data["id"] : null;
        if (newProjectID) {
          providedTableName
            ? dispatch(
                addToCustomProject({
                  productID: providedTableName,
                  tableDesc: tableDesc,
                  productURL: productURL,
                  projectID: newProjectID,
                  artifact: 3,
                })
              )
            : dispatch(
                addToCustomProject({
                  productID: productID,
                  productURL: productURL,
                  projectID: newProjectID,
                  projectName: projectTitle,
                  artifact: 0,
                })
              );
          dispatch(
            getProjects({ init: false, translationValue: translationValue })
          );
        }
      },
      (error) =>
        commonModalErrorHandler(
          error,
          dispatch,
          translationValue,
          productID,
          projectTitle
        ),
      dispatch
    );
};
